import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { StammkundeService } from './stammkunde.service';
import { EinsatzMaterial, ZeitEintragCreate } from '../Models/stammkunde.model';

export type QueuedCaptureKind = 'time-entry' | 'work-status' | 'materials';

/** Ein zwischengespeicherter Erfassungsvorgang (offline auf der Baustelle). */
export interface QueuedCapture {
  id: string;
  kind: QueuedCaptureKind;
  auftragId: number;
  assignmentId: number | null;
  payload: any;
  /** Kurzbeschreibung für die Warteschlangen-Anzeige. */
  label: string;
  createdAt: number;
}

export interface CaptureResult {
  /** true = offline in die Warteschlange gelegt; false = direkt gesendet. */
  queued: boolean;
  result?: any;
}

export interface FlushSummary {
  sent: number;
  /** Verworfene Einträge (fachlicher Fehler, z.B. Einsatz gelöscht/gesperrt). */
  dropped: number;
  remaining: number;
}

/**
 * Offline-fähige Erfassung für den Vorarbeiter: Stunden, Arbeitsstatus und Material
 * werden bei fehlender Verbindung lokal (localStorage) zwischengespeichert und beim
 * Wiederherstellen der Verbindung automatisch nachgesendet. Fachliche Fehler
 * (z.B. der Einsatz wurde gelöscht) verwerfen den betroffenen Eintrag – ein reiner
 * Verbindungsfehler lässt ihn in der Warteschlange.
 */
@Injectable({ providedIn: 'root' })
export class CaptureQueueService {
  private readonly storageKey = 'sk_capture_queue_v1';
  private queue: QueuedCapture[] = [];

  private pendingSubject = new BehaviorSubject<QueuedCapture[]>([]);
  pending$: Observable<QueuedCapture[]> = this.pendingSubject.asObservable();

  private onlineSubject = new BehaviorSubject<boolean>(this.readOnline());
  online$: Observable<boolean> = this.onlineSubject.asObservable();

  private flushing = false;

  constructor(private stammkundeService: StammkundeService) {
    this.queue = this.readQueue();
    this.pendingSubject.next([...this.queue]);

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.onlineSubject.next(true);
        this.flush();
      });
      window.addEventListener('offline', () => this.onlineSubject.next(false));
    }
  }

  get isOnline(): boolean {
    return this.readOnline();
  }

  get pendingCount(): number {
    return this.queue.length;
  }

  // ── Erfassung (online-first, sonst Warteschlange) ──

  captureTimeEntry(
    auftragId: number, assignmentId: number | null, payload: ZeitEintragCreate, label: string): Promise<CaptureResult> {
    return this.capture('time-entry', auftragId, assignmentId, payload, label,
      () => firstValueFrom(this.stammkundeService.addTimeEntry(auftragId, payload)));
  }

  captureWorkStatus(
    auftragId: number, assignmentId: number, status: string, label: string): Promise<CaptureResult> {
    return this.capture('work-status', auftragId, assignmentId, { status }, label,
      () => firstValueFrom(this.stammkundeService.setAssignmentWorkStatus(auftragId, assignmentId, status)));
  }

  captureMaterials(
    auftragId: number, assignmentId: number, materials: EinsatzMaterial[], label: string): Promise<CaptureResult> {
    return this.capture('materials', auftragId, assignmentId, materials, label,
      () => firstValueFrom(this.stammkundeService.setAssignmentMaterials(auftragId, assignmentId, materials)));
  }

  private async capture(
    kind: QueuedCaptureKind, auftragId: number, assignmentId: number | null,
    payload: any, label: string, send: () => Promise<any>): Promise<CaptureResult> {

    if (!this.readOnline()) {
      this.enqueue({ kind, auftragId, assignmentId, payload, label });
      return { queued: true };
    }

    try {
      const result = await send();
      return { queued: false, result };
    } catch (err) {
      if (this.isNetworkError(err)) {
        this.enqueue({ kind, auftragId, assignmentId, payload, label });
        return { queued: true };
      }
      throw err; // echter Fehler (Validierung/Recht) – an den Aufrufer weiterreichen
    }
  }

  // ── Nachsenden ──

  /** Warteschlange abarbeiten. Läuft bei einem Verbindungsfehler geordnet aus. */
  async flush(): Promise<FlushSummary> {
    if (this.flushing || !this.readOnline() || this.queue.length === 0) {
      return { sent: 0, dropped: 0, remaining: this.queue.length };
    }
    this.flushing = true;
    let sent = 0, dropped = 0;
    try {
      for (const item of [...this.queue]) {
        try {
          await this.dispatch(item);
          this.remove(item.id);
          sent++;
        } catch (err) {
          if (this.isNetworkError(err)) {
            break; // weiterhin offline – Rest bleibt in der Warteschlange
          }
          this.remove(item.id); // fachlicher Fehler – Eintrag verwerfen
          dropped++;
        }
      }
    } finally {
      this.flushing = false;
    }
    return { sent, dropped, remaining: this.queue.length };
  }

  private dispatch(item: QueuedCapture): Promise<any> {
    switch (item.kind) {
      case 'time-entry':
        return firstValueFrom(this.stammkundeService.addTimeEntry(item.auftragId, item.payload));
      case 'work-status':
        return firstValueFrom(
          this.stammkundeService.setAssignmentWorkStatus(item.auftragId, item.assignmentId!, item.payload.status));
      case 'materials':
        return firstValueFrom(
          this.stammkundeService.setAssignmentMaterials(item.auftragId, item.assignmentId!, item.payload));
      default:
        return Promise.resolve();
    }
  }

  // ── Warteschlangen-Verwaltung ──

  private enqueue(item: Omit<QueuedCapture, 'id' | 'createdAt'>): void {
    const entry: QueuedCapture = {
      ...item,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      createdAt: Date.now()
    };
    this.queue.push(entry);
    this.persist();
  }

  private remove(id: string): void {
    this.queue = this.queue.filter(q => q.id !== id);
    this.persist();
  }

  private persist(): void {
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(this.queue));
      } catch { /* Speicher voll/gesperrt – bestenfalls im RAM halten */ }
    }
    this.pendingSubject.next([...this.queue]);
  }

  private readQueue(): QueuedCapture[] {
    if (typeof localStorage === 'undefined') {
      return [];
    }
    try {
      const raw = localStorage.getItem(this.storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private readOnline(): boolean {
    return typeof navigator === 'undefined' ? true : navigator.onLine;
  }

  /** status 0 (bzw. offline) = Verbindungsfehler; alles andere ist ein echter Serverfehler. */
  private isNetworkError(err: any): boolean {
    if (!this.readOnline()) {
      return true;
    }
    return !!err && typeof err === 'object' && 'status' in err && err.status === 0;
  }
}
